import type {
  AutoWithdrawalDto,
  AutoWithdrawalNetworkCode,
  AutoWithdrawalRuleDto,
} from '@shared/api/contracts';
import { getAutoWithdrawalPairKey } from '../model/auto-withdrawal';

const NULLISH_STRINGS = new Set(['null', 'undefined', '']);

const normalizeNullableString = (value: unknown): string | null => {
  if (value == null) return null;
  const trimmed = String(value).trim();
  if (!trimmed || NULLISH_STRINGS.has(trimmed.toLowerCase())) return null;
  return trimmed;
};

export const normalizeLegacyNetworkCode = (network: string | null | undefined): AutoWithdrawalNetworkCode | null => {
  if (!network) return null;
  const normalized = network.trim().toUpperCase().replace(/-/g, '');
  const map: Record<string, AutoWithdrawalNetworkCode> = {
    TRC20: 'TRC20',
    TRON: 'TRC20',
    ERC20: 'ERC20',
    ETHEREUM: 'ERC20',
    ETH: 'ERC20',
    BEP20: 'BEP20',
    BSC: 'BEP20',
    BTC: 'BTC',
    LTC: 'LTC',
  };
  return map[normalized] ?? null;
};

const buildLegacyRule = (dto: AutoWithdrawalDto): AutoWithdrawalRuleDto | null => {
  const networkCode = normalizeLegacyNetworkCode(dto.network);
  const destinationAddress = normalizeNullableString(dto.address);
  const thresholdRaw = dto.amount;

  if (!networkCode || !destinationAddress || thresholdRaw == null) {
    return null;
  }

  return {
    id: dto.id,
    assetCode: 'USDT',
    networkCode,
    destinationAddress,
    thresholdAmount: String(thresholdRaw),
    amountMode: 'FULL_BALANCE',
    reserveAmount: null,
    enabled: true,
    hasVerification: false,
    sortOrder: 0,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  };
};

export const normalizeAutoWithdrawalDto = (dto: AutoWithdrawalDto | null): AutoWithdrawalDto | null => {
  if (!dto) return null;

  const hasRules = Array.isArray(dto.rules) && dto.rules.length > 0;
  if (hasRules) {
    return {
      ...dto,
      routingMode: dto.routingMode ?? 'SINGLE_TARGET_SWAP',
      network: normalizeNullableString(dto.network),
      address: normalizeNullableString(dto.address),
      amount: dto.amount == null || NULLISH_STRINGS.has(String(dto.amount).toLowerCase()) ? null : dto.amount,
      rules: dto.rules!.map((rule) => ({
        ...rule,
        destinationAddress: normalizeNullableString(rule.destinationAddress) ?? '',
        thresholdAmount: String(rule.thresholdAmount ?? ''),
        reserveAmount:
          rule.reserveAmount == null || NULLISH_STRINGS.has(String(rule.reserveAmount).toLowerCase())
            ? null
            : String(rule.reserveAmount),
      })),
    };
  }

  const legacyRule = buildLegacyRule(dto);
  if (!legacyRule) {
    return {
      ...dto,
      routingMode: 'SINGLE_TARGET_SWAP',
      rules: [],
      network: normalizeNullableString(dto.network),
      address: normalizeNullableString(dto.address),
      amount: dto.amount == null ? null : dto.amount,
    };
  }

  return {
    ...dto,
    routingMode: 'SINGLE_TARGET_SWAP',
    rules: [legacyRule],
    network: legacyRule.networkCode,
    address: legacyRule.destinationAddress,
    amount: legacyRule.thresholdAmount,
  };
};

export const findRuleByPairKey = (
  rules: AutoWithdrawalRuleDto[],
  pairKey: string,
): AutoWithdrawalRuleDto | undefined =>
  rules.find((rule) => getAutoWithdrawalPairKey(rule.assetCode, rule.networkCode) === pairKey);
